"use client";

import * as React from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { Text } from "@/components/ui/text";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

interface DetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  children?: React.ReactNode;
  className?: string;
  side?: "left" | "right" | "top" | "bottom";
  /** Default footer about AI-generated insights */
  showAiDisclaimer?: boolean;
}

/**
 * Reusable detail drawer component
 * Can be used for displaying any detail content in a side panel
 */
export const DetailDrawer: React.FC<DetailDrawerProps> = ({
  open,
  onOpenChange,
  title,
  children,
  className,
  side = "right",
  showAiDisclaimer = true,
}) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={side} className={cn("w-full sm:max-w-2xl overflow-y-auto flex flex-col", className)}>
        {title ? (
          <>
            <SheetTitle className="mb-6">
              <div className="mb-2">
                <Text as="span" size="xl" weight="semibold" color="default">
                  {title}
                </Text>
              </div>
              <Separator />
            </SheetTitle>
            <div className="space-y-6 flex-1">
              {children}
            </div>
          </>
        ) : (
          <>
            <VisuallyHidden>
              <SheetTitle>Drawer</SheetTitle>
            </VisuallyHidden>
            <div className="space-y-6 flex-1">
              {children}
            </div>
          </>
        )}
        {showAiDisclaimer ? (
          <p className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground shrink-0 italic">
            * AI-generated insights are based on available data and should be reviewed before sharing externally.
          </p>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

interface DetailDrawerSkeletonProps {
  title?: boolean;
}

export const DetailDrawerSkeleton: React.FC<DetailDrawerSkeletonProps> = ({ title = true }) => {
  return (
    <div className="space-y-6">
      {title && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}
      <div className="space-y-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-6 w-1/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
      </div>
    </div>
  );
};

interface SourceLinkProps {
  name: string;
  url: string;
  type?: string;
}

export const SourceLink: React.FC<SourceLinkProps> = ({ name, url, type: _type }) => {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
    >
      <Text size="sm" weight="medium" color="default">
        {name}
      </Text>
      <ExternalLink className="w-4 h-4" />
    </a>
  );
};

